/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IIconProps, IIconStyleProps, IIconStyles, Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Badge } from 'src/core/components/Badge';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { getSimpleEntityLimits } from 'src/luis/modules/entities/utils/entityCreationLimits';
import { getTranslatedEntityType } from 'src/luis/modules/entities/utils/translatedEntityType';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { depthColorsMap } from 'src/modules/labeling/utils/labelingConstants';

/**
 * Width used to represent indentation in the tree
 */
const indentWidth = 18;
const initialPadding = 12;

const messages = defineMessages({
    addChildTitle: {
        id: 'luis.entities.entityTreeItem.addChildTitle',
        defaultMessage: 'Add child'
    },
    addChildMaxCountTitle: {
        id: 'luis.entities.entityTreeItem.addChildMaxCountTitle',
        defaultMessage: 'You have reached the maximum number of children for this entity'
    },
    deleteEntityTitle: {
        id: 'luis.entities.entityTreeItem.deleteEntityTitle',
        defaultMessage: 'Delete'
    },
    collapseEntityTitle: {
        id: 'luis.entities.entityTreeItem.collapseEntityTitle',
        defaultMessage: 'Collapse'
    },
    expandEntityTitle: {
        id: 'luis.entities.entityTreeItem.expandEntityTitle',
        defaultMessage: 'Expand'
    },
    hasRequiredFeature: {
        id: 'luis.entities.entityTreeItem.hasRequiredFeature',
        defaultMessage: 'Entity has a required feature'
    },
    hasFeatures: {
        id: 'luis.entities.entityTreeItem.hasFeatures',
        defaultMessage: 'Entity has features'
    }
});

const { styleComponent, getClassName } = getStylistV2('EntityTreeItem');
const scopedTheme = getScopedTheme('EntityTreeItem');

const entityActionsContainerClassName = getClassName('EntityActionsContainer', { opacity: 0, transition: 'opacity 150ms ease' });

const EntityContainer = styleComponent<IStackProps>(Stack)(
    'EntityContainer',
    (_, cssVars) => ({
        backgroundColor: cssVars.backgroundColor,
        paddingLeft: cssVars.paddingLeft,
        paddingRight: initialPadding,
        cursor: 'pointer',
        $nest: {
            '&:hover': {
                backgroundColor: cssVars.hoveredBackgroundColor,
                $nest: {
                    [`& .${entityActionsContainerClassName}`]: {
                        opacity: 1
                    }
                }
            },
            '&:focus': {
                backgroundColor: cssVars.hoveredBackgroundColor,
                $nest: {
                    [`& .${entityActionsContainerClassName}`]: {
                        opacity: 1
                    }
                }
            },
            '&:focus-within': {
                backgroundColor: cssVars.hoveredBackgroundColor,
                $nest: {
                    [`& .${entityActionsContainerClassName}`]: {
                        opacity: 1
                    }
                }
            }
        }
    }),
    {
        cssVars: { paddingLeft: '', backgroundColor: '', hoveredBackgroundColor: '' },
        cssVarsSetter: ({ level, isSelected }: { level: number; isSelected: boolean }, theme) => ({
            paddingLeft: `${indentWidth * level + initialPadding}px`,
            backgroundColor: isSelected ? theme.selectedBackgroundColor : 'transparent',
            hoveredBackgroundColor: isSelected ? theme.selectedBackgroundColor : theme.hoveredBackgroundColor
        })
    }
);

const controlsButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { width: 24, height: 24 },
    rootHovered: { backgroundColor: scopedTheme.buttonHoverColor },
    iconDisabled: { opacity: 0.4 }
});

const textFieldStyles = classNamesFunction<ITextFieldProps, ITextFieldStyles>()({
    fieldGroup: {
        flex: 1,
        height: 24
    }
});

const getIconClassNames = classNamesFunction<IIconStyleProps, IIconStyles>();
const sharedIconStyles = { fontSize: '12px' };

const ColorIcon = styleComponent<IIconProps>(Icon)(
    'ColorIcon',
    (_, cssVars) => ({
        color: cssVars.color
    }),
    {
        cssVars: { color: '' },
        cssVarsSetter: ({ entityType, level }: { entityType: ModelType; level: number }, theme) => ({
            color: isMachineLearned(entityType) ? depthColorsMap[level + 1] : theme.defaultColor
        })
    }
);

const ToggleCollapseButton = styleComponent<IButtonProps>(IconButton)(
    'ToggleCollapseButton',
    (_, cssVars) =>
        ({
            fontSize: '12px',
            transform: cssVars.transform,
            transition: 'transform 150ms ease',
            visibility: cssVars.visibility as Property.Visibility
        } as Record<string, string>),
    {
        cssVars: { transform: '', visibility: 'visible' },
        cssVarsSetter: ({ isCollapsed, hasChildren }: { isCollapsed: boolean; hasChildren: boolean }) => ({
            transform: isCollapsed ? '' : 'rotate(180deg)',
            visibility: hasChildren ? 'visible' : 'hidden'
        })
    }
);

const addChildEntityButtonIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, color: scopedTheme.addChildEntityButtonColor }
});

const deleteEntityButtonIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, color: scopedTheme.deleteEntityButtonColor }
});

const toggleEntityButtonIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, color: scopedTheme.toggleEntityButtonColor }
});

const featuresIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, marginLeft: '8px' }
});

const requiredFeaturesIconStyles = getIconClassNames({
    root: { ...sharedIconStyles, fontSize: 10 }
});

/**
 * Props for EntityTreeItem
 */
type EntityTreeItemProps = {
    /**
     * For ML child entities, the id of the root
     */
    rootEntityId: LuisId;
    /**
     * For ML child entities, the id of the direct parent
     */
    parentEntityId: LuisId;
    /**
     * The entity being displayed
     */
    entityStore: EntityStore;
    /**
     * For ML entities, the level of nesting in the tree structure.
     */
    level: number;
    /**
     * For ML entities, whether a parent is collapsed.
     */
    isCollapsed: boolean;
    /**
     * Used for having a "dummy row" when creating a new child entity.
     */
    kind: 'newEntity' | 'existingEntity';
    /**
     * Whether the entity displayed is currently selected.
     */
    isSelected: boolean;
    /**
     * Whether entities at the top level can be deleted by the user from the tree
     */
    allowRootEntityDeletion?: boolean;
    /**
     * Used to add / remove a new "dummy row" for creating children.
     */
    onToggleEntityCreation: (parentId: LuisId) => void;
    /**
     * Callback for collapsing a parent Ml entity.
     */
    onToggleCollapse: (id: string) => void;
    /**
     * Callback for when a child ML entity is created from a "dummy row".
     */
    onCreateEntity: (rootEntityId: LuisId, parentEntityId: LuisId, entityName: string) => void;
    /**
     * Callback for when an entity is renamed (root).
     */
    onRenameEntity: (entityStore: EntityStore, newName: string) => void;
    /**
     * Callback for when an entity is renamed (children).
     */
    onRenameChildEntity: (rootEntityId: LuisId, entityId: LuisId, newName: string) => void;
    /**
     * Callback for when an entity is deleted.
     */
    onDeleteEntity: (rootEntityId: LuisId, entityStore: EntityStore) => void;
    /**
     * Callback for rendering an entity content.
     */
    onRenderEntityContent: (entity: EntityStore) => React.ReactNode;
    /**
     * Callback for selecting the entity.
     */
    onSelectEntity?: () => void;
};

export const EntityTreeItem = observer((props: EntityTreeItemProps) => {
    const {
        rootEntityId,
        parentEntityId,
        entityStore,
        level,
        isCollapsed,
        kind,
        isSelected,
        allowRootEntityDeletion,
        onSelectEntity,
        onToggleCollapse,
        onToggleEntityCreation,
        onCreateEntity,
        onDeleteEntity,
        onRenameEntity,
        onRenameChildEntity,
        onRenderEntityContent
    } = props;

    const { localize } = useLocalization();

    const [isEditingName, setIsEditingName] = React.useState(kind === 'newEntity');
    const [currentName, setCurrentName] = React.useState(entityStore.name);

    const isMLEntity = entityStore.typeId === ModelType.Simple;

    const numberOfChildren = isMLEntity && (entityStore as SimpleEntityStore).children.length;
    const hasChildren = numberOfChildren > 0;

    const { canHaveChildrenBasedOfDepth, canHaveChildrenBasedOfCurrentCount } = getSimpleEntityLimits(entityStore, level);
    const canBeDeleted = level !== 0 || allowRootEntityDeletion;

    const hasFeatures =
        (isMLEntity && (entityStore as SimpleEntityStore).featureStoreHouse.items.length) || (entityStore as SimpleEntityStore).instanceOf;

    const hasRequiredFeature =
        (isMLEntity && (entityStore as SimpleEntityStore).featureStoreHouse.items.some(f => f.isRequired)) ||
        (entityStore as SimpleEntityStore).instanceOf;

    const handleEntityDoubledClicked = () => {
        if (entityStore.typeId !== ModelType.Prebuilt) {
            setIsEditingName(true);
        }
    };

    const handleRenameEntity = () => {
        setIsEditingName(false);

        switch (kind) {
            case 'existingEntity':
                if (currentName === '' || currentName === entityStore.name) {
                    setCurrentName(entityStore.name);

                    return;
                }

                if (level === 0) {
                    onRenameEntity(entityStore, currentName);
                } else {
                    onRenameChildEntity(rootEntityId, entityStore.id, currentName);
                }
                break;

            case 'newEntity':
                if (currentName !== '') {
                    onCreateEntity(rootEntityId, parentEntityId, currentName);
                } else {
                    onToggleEntityCreation(null);
                }

                break;

            default:
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === LuisKeyCodes.Escape) {
            switch (kind) {
                case 'existingEntity':
                    setIsEditingName(false);
                    setCurrentName(entityStore.name);
                    break;

                case 'newEntity':
                    onToggleEntityCreation(null);
                    break;

                default:
            }
        }
    };

    const renderAddChildButton = () => {
        const addChildButtonBase = (
            <IconButton
                title={localize(messages.addChildTitle)}
                onClick={e => {
                    e.stopPropagation();
                    if (isCollapsed) {
                        onToggleCollapse(entityStore.id as string);
                    }
                    onToggleEntityCreation(entityStore.id);
                }}
                className={isSelected ? null : entityActionsContainerClassName}
                styles={controlsButtonStyles}
                iconProps={{ iconName: 'Add', styles: addChildEntityButtonIconStyles }}
                disabled={isEditingName || !canHaveChildrenBasedOfCurrentCount}
                autoFocus={false}
            />
        );

        if (!canHaveChildrenBasedOfCurrentCount) {
            return <LuisTooltip content={localize(messages.addChildMaxCountTitle)}>{addChildButtonBase}</LuisTooltip>;
        }

        return addChildButtonBase;
    };

    return (
        <EntityContainer
            data-is-focusable
            onClick={onSelectEntity}
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
            customProps={{ level, isSelected }}>
            <ColorIcon iconName="StatusCircleInner" customProps={{ entityType: entityStore.typeId, level }} />
            <Stack
                styles={{ root: { overflow: 'hidden' } }}
                grow
                verticalFill
                verticalAlign="center"
                horizontal
                tokens={{ childrenGap: 8 }}>
                {isEditingName ? (
                    <LuisTextField
                        styles={textFieldStyles}
                        autoFocus
                        value={currentName}
                        onChange={(_, newValue) => setCurrentName(newValue)}
                        onEnterKeyUp={e => {
                            e.preventDefault();
                            handleRenameEntity();
                        }}
                        onBlur={e => {
                            e.preventDefault();
                            handleRenameEntity();
                        }}
                        onKeyDown={onKeyDown}
                    />
                ) : (
                    <LuisText
                        styles={{ root: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        title={entityStore.name}
                        onDoubleClick={handleEntityDoubledClicked}>
                        {onRenderEntityContent(entityStore)}
                        {entityStore.typeId !== ModelType.Simple && <Badge text={getTranslatedEntityType(entityStore.typeId, localize)} />}
                    </LuisText>
                )}
                {hasFeatures && <Icon title={localize(messages.hasFeatures)} iconName="Link" styles={featuresIconStyles} />}
                {hasRequiredFeature && (
                    <Icon title={localize(messages.hasRequiredFeature)} iconName="Asterisk" styles={requiredFeaturesIconStyles} />
                )}
            </Stack>
            {kind === 'existingEntity' && (
                <>
                    {(canBeDeleted || canHaveChildrenBasedOfDepth) && !isEditingName ? (
                        <Stack horizontal horizontalAlign="end">
                            {canHaveChildrenBasedOfDepth && renderAddChildButton()}

                            {canBeDeleted && (
                                <IconButton
                                    title={localize(messages.deleteEntityTitle)}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onDeleteEntity(rootEntityId, entityStore);
                                    }}
                                    className={isSelected ? null : entityActionsContainerClassName}
                                    styles={controlsButtonStyles}
                                    iconProps={{ iconName: 'Delete', styles: deleteEntityButtonIconStyles }}
                                    disabled={isEditingName}
                                    autoFocus={false}
                                />
                            )}
                        </Stack>
                    ) : null}
                    <ToggleCollapseButton
                        disabled={!hasChildren}
                        title={isCollapsed ? localize(messages.expandEntityTitle) : localize(messages.collapseEntityTitle)}
                        iconProps={{ iconName: 'ChevronDown', styles: toggleEntityButtonIconStyles }}
                        customProps={{ isCollapsed, hasChildren }}
                        styles={controlsButtonStyles}
                        onClick={e => {
                            e.stopPropagation();
                            onToggleCollapse(entityStore.id as string);
                        }}
                    />
                </>
            )}
        </EntityContainer>
    );
});
